import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const MAX_SIZE_KB = 64;
const MAX_SIZE_BYTES = MAX_SIZE_KB * 1024;

const CodeEditor = ({ language, value, onChange, maxSize = MAX_SIZE_BYTES }) => {
  const [size, setSize] = useState(0);
  const [isOverLimit, setIsOverLimit] = useState(false);
  const [isNearLimit, setIsNearLimit] = useState(false);

  useEffect(() => {
    const byteSize = new Blob([value || '']).size;
    setSize(byteSize);
    setIsOverLimit(byteSize > maxSize);
    setIsNearLimit(byteSize > maxSize * 0.8);
  }, [value, maxSize]);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getLanguageLabel = () => {
    const labels = {
      html: 'HTML',
      css: 'CSS',
      javascript: 'JavaScript',
    };
    return labels[language] || language.toUpperCase();
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    const newSize = new Blob([newValue]).size;
    
    if (newSize > maxSize) {
      setIsOverLimit(true);
      return;
    }
    
    setIsOverLimit(false);
    onChange(newValue);
  };

  const getTextareaClass = () => {
    let baseClass = 'font-mono text-sm w-full h-64 p-3 border rounded-lg focus:outline-none focus:ring-2 resize-y ';
    
    if (isOverLimit) {
      baseClass += 'border-red-500 focus:ring-red-200 bg-red-50';
    } else if (isNearLimit) {
      baseClass += 'border-yellow-500 focus:ring-yellow-200';
    } else {
      baseClass += 'border-gray-300 focus:ring-primary-200';
    }
    
    return baseClass;
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <label className="label mb-0">
          {getLanguageLabel()}
          <span className="text-gray-400 text-xs ml-2">
            (max {formatSize(maxSize)})
          </span>
        </label>
        <span className={`text-xs font-mono ${
          isOverLimit ? 'text-red-600 font-bold' : 
          isNearLimit ? 'text-yellow-600' : 'text-gray-500'
        }`}>
          {formatSize(size)} / {formatSize(maxSize)}
          {isOverLimit && ' ⚠️'}
        </span>
      </div>
      
      <textarea
        value={value}
        onChange={handleChange}
        className={getTextareaClass()}
        placeholder={`Ingresa tu código ${getLanguageLabel()} aquí...`}
        spellCheck={false}
        disabled={isOverLimit}
      />
      
      {isOverLimit && (
        <p className="text-red-600 text-sm">
          El contenido excede el límite de {formatSize(maxSize)}. 
          Por favor reduce el tamaño del código.
        </p>
      )}
    </div>
  );
};

CodeEditor.propTypes = {
  language: PropTypes.oneOf(['html', 'css', 'javascript']).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  maxSize: PropTypes.number,
};

export default CodeEditor;
